import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import TollIcon from "@mui/icons-material/Toll";
import { createTipOfDay, updateTipOfDay } from "../../service/service";
import { COLORS, TYPE_META, modalBackdropSlotProps } from "./colors";

// Reward is always one of these 10 values. Defaults to the lowest (50) rather than a random pick
// — a coach who forgets to check the dropdown before saving accidentally grants the smallest
// reward instead of an arbitrarily large one. Still freely changeable via the dropdown.
const REWARD_OPTIONS = [50, 100, 150, 200, 250, 300, 350, 400, 450, 500];
const DEFAULT_REWARD = REWARD_OPTIONS[0];

// Matches the backend's accepted extensions/size cap for tip audio uploads.
const MAX_AUDIO_SIZE = 50 * 1024 * 1024;
const ACCEPTED_AUDIO_EXTENSIONS = ["mp3", "wav"];

// A tip's date is never picked here — a new tip auto-joins the back of the rotation queue, and
// an existing one's date only ever changes via the daily rotation or a manual reorder.
const emptyForm = () => ({
  type: "audio",
  title: "",
  description: "",
  audioFile: null,
  reward: DEFAULT_REWARD,
});

const formFromTip = (tip) => ({
  type: tip.type,
  title: tip.title,
  description: tip.description,
  audioFile: null,
  reward: tip.reward,
});

const AddTipModal = ({ open, onClose, onSaved, tip }) => {
  const isEditMode = Boolean(tip);
  const [form, setForm] = useState(() => emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [audioFileError, setAudioFileError] = useState("");
  const [toast, setToast] = useState({ open: false, message: "" });

  useEffect(() => {
    if (open) {
      setForm(isEditMode ? formFromTip(tip) : emptyForm());
      setError("");
      setAudioFileError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tip]);

  const handleClose = () => {
    onClose();
  };

  const handleTypeChange = (event, newType) => {
    if (newType) {
      setForm((prev) => ({ ...prev, type: newType, audioFile: newType === "text" ? null : prev.audioFile }));
    }
  };

  const applyAudioFile = (file) => {
    if (!file) return;
    const extension = file.name.split(".").pop().toLowerCase();
    if (!ACCEPTED_AUDIO_EXTENSIONS.includes(extension)) {
      setAudioFileError("Only MP3 and WAV files are accepted.");
      return;
    }
    if (file.size > MAX_AUDIO_SIZE) {
      setAudioFileError("Audio file must be under 50MB.");
      return;
    }
    setAudioFileError("");
    setForm((prev) => ({ ...prev, audioFile: file }));
  };

  const handleFileChange = (event) => {
    applyAudioFile(event.target.files?.[0] || null);
  };

  const handleFileDrop = (event) => {
    event.preventDefault();
    applyAudioFile(event.dataTransfer.files?.[0] || null);
  };

  const hasExistingAudio = isEditMode && tip.type === "audio" && tip.audioUrl;
  const needsNewAudioFile = form.type === "audio" && !form.audioFile && !hasExistingAudio;

  const isFormValid = form.title.trim() && form.description.trim() && !needsNewAudioFile;

  const audioHelperText = form.audioFile
    ? form.audioFile.name
    : hasExistingAudio
    ? "or drag and drop to replace the existing audio"
    : "or drag and drop";

  const handleSubmit = async () => {
    setSubmitting(true);
    setError("");
    const result = isEditMode ? await updateTipOfDay(tip.id, form) : await createTipOfDay(form);
    setSubmitting(false);

    if (result?.success === false) {
      setError(result.error?.message || result.error?.err || "Failed to save tip. Please try again.");
      return;
    }

    onSaved?.(result?.data);
    handleClose();
    if (!isEditMode) {
      setToast({ open: true, message: "Tip added successfully." });
    }
  };

  return (
    <>
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" slotProps={modalBackdropSlotProps}>
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {isEditMode ? "Edit Tip" : "Add New Tip"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isEditMode ? "Update this tip's details" : "Adds to the back of the rotation queue"}
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Type
        </Typography>
        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          {["audio", "text"].map((typeOption) => {
            const meta = TYPE_META[typeOption];
            const selected = form.type === typeOption;
            return (
              <Box
                key={typeOption}
                role="button"
                tabIndex={0}
                onClick={() => handleTypeChange(null, typeOption)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") handleTypeChange(null, typeOption);
                }}
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  py: 1.25,
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: selected ? meta.color : COLORS.border,
                  bgcolor: selected ? meta.bg : "#FFFFFF",
                  color: selected ? meta.color : COLORS.secondaryText,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                {typeOption === "audio" ? (
                  <VolumeUpIcon sx={{ fontSize: 18 }} />
                ) : (
                  <Typography component="span" sx={{ fontWeight: 700, fontSize: 15 }}>
                    T
                  </Typography>
                )}
                {meta.label}
              </Box>
            );
          })}
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Title
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="e.g. Master the Sounds: Flap 'T' and Light vs. Dark 'L'"
          value={form.title}
          onChange={(event) => setForm((prev) => ({ ...prev, title: event.target.value }))}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          Description
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          placeholder="The full tip content shown inside the app…"
          value={form.description}
          onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
          sx={{ mb: 2 }}
        />

        {form.type === "audio" && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Audio File
            </Typography>
            <Box
              onDragOver={(event) => event.preventDefault()}
              onDrop={handleFileDrop}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                p: 1.5,
                border: "1px dashed",
                borderColor: COLORS.border,
                borderRadius: 1.5,
              }}
            >
              <Button
                component="label"
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                sx={{ color: COLORS.primaryText, borderColor: COLORS.primaryText, whiteSpace: "nowrap" }}
              >
                Upload Audio
                <input type="file" accept="audio/*" hidden onChange={handleFileChange} />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }} noWrap>
                {form.audioFile ? form.audioFile.name : audioHelperText}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              MP3, WAV up to 50MB
            </Typography>
            {audioFileError && (
              <Typography variant="body2" sx={{ color: COLORS.destructive, mt: 0.5 }}>
                {audioFileError}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ flex: "1 1 160px" }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Gold Reward
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Select
              fullWidth
              size="small"
              value={form.reward}
              onChange={(event) => setForm((prev) => ({ ...prev, reward: event.target.value }))}
            >
              {REWARD_OPTIONS.map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
            <TollIcon sx={{ fontSize: 18, color: COLORS.gold }} />
          </Box>
        </Box>

        {error && (
          <Typography variant="body2" sx={{ color: COLORS.destructive, mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          sx={{ color: COLORS.primaryText, borderColor: COLORS.primaryText }}
          onClick={handleClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!isFormValid || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {isEditMode ? "Save Changes" : "Create Tip"}
        </Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={toast.open}
      autoHideDuration={4000}
      onClose={() => setToast({ open: false, message: "" })}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert onClose={() => setToast({ open: false, message: "" })} severity="success" sx={{ width: "100%" }}>
        {toast.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default AddTipModal;
